import { Typography } from 'components/generic/typography';
import styled from 'styled-components';
import BasketMessageContainer from '../BasketMessage/shared/styles';
import {
    DeleteBasketStore,
    UpdateBasketStore,
    type BasketDeliveryFixViewModel,
    type ClickAndCollectBasketStoreViewModel,
    type OnlineDeliveryOption
} from 'autogen/swagger/all';
import format from 'helpers/translations';
import { useState } from 'react';
import {
    ControlledSegmentedButtons,
    type ControlledSegmentedButtonsProps
} from 'components/SegmentedButtons/SegmentedButtons';
import { useTranslation } from 'react-i18next';
import {
    CancelButton,
    ClickAndCollectTabTitle,
    ContinueButton,
    ModalHeading,
    NoDeliveryTabSelected,
    OnlineDeliveryTabTitle
} from 'autogen/translation-keys/trans-website-basket-deliveryfix';
import ClickAndCollectStores from './components/ClickAndCollectStores';
import OnlineDelivery from './components/OnlineDelivery';
import HighlightedText from 'components/generic/HighlightedText';
import { customScrollbar } from 'modules/helpers/mixins';
import FlexColumn from 'components/Layout/components/Flex/components/FlexColumn';
import ActionFooter from 'components/quickView/components/ActionFooter';
import { Button } from 'components/Button';
import { useQuickView, useSlides } from 'components/quickView';
import RemovedProductItems from './components/RemovedProductItems';
import { updateBasket } from 'components/basket/shared/basket';
import { useDeliveryFixStore } from '../../shared/deliveryFixStore';
import { useShallow } from 'zustand/shallow';
import useGA4Tracking from 'modules/tracking/GA4/GA4Tracking';

export type AntiBreakQuickViewContentProps = {
    model: BasketDeliveryFixViewModel;
};

const StyledFlexColumn = styled(FlexColumn)`
    width: 100%;
`;

const ScrollContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.Spacing24};
    padding: ${({ theme }) => `0 ${theme.Spacing24} ${theme.Spacing24}`};
    overflow: hidden scroll;
    -webkit-overflow-scrolling: touch;
    ${customScrollbar}
`;

const StyledBasketMessageContainer = styled(BasketMessageContainer)`
    background-color: ${({ theme }) => theme.ColorBackgroundWarningLight};
`;

const DeliveryOptionsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.Spacing24};
    padding: ${({ theme }) => theme.Spacing16};
    background: ${({ theme }) => theme.ColorBackgroundSecondary};
    border-radius: ${({ theme }) => theme.RadiusMedium};
`;

const ButtonContainer = styled.div`
    display: grid;
    width: 100%;
    column-gap: ${({ theme }) => theme.Spacing8};
    grid-template-columns: 1fr 1fr;
`;

function AntiBreakQuickViewContent(props: AntiBreakQuickViewContentProps) {
    const { model } = props;
    const { clickAndCollectDeliveryOption, onlineDeliveryOption, disclaimer } = model;
    const [selectedTab, setSelectedTab] = useState<number>(1);
    const [selectedDeliveryMethodId, setSelectedDeliveryMethodId] = useState<string | number | undefined>();
    const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState<
        OnlineDeliveryOption | ClickAndCollectBasketStoreViewModel | undefined
    >();
    const { t } = useTranslation();
    const { push } = useSlides();
    const { toggle } = useQuickView('antiBreakQuickView');
    const { setShowDeliveryFixBasketMessage } = useDeliveryFixStore(
        useShallow(({ setShowDeliveryFixBasketMessage }) => ({
            setShowDeliveryFixBasketMessage
        }))
    );
    const GA4Tracking = useGA4Tracking();

    function isClickAndCollectDelivery(
        model: OnlineDeliveryOption | ClickAndCollectBasketStoreViewModel | undefined
    ): model is ClickAndCollectBasketStoreViewModel {
        return (model as ClickAndCollectBasketStoreViewModel).model !== undefined;
    }

    const buttons: ControlledSegmentedButtonsProps['buttons'] = [
        {
            buttonId: 1,
            children: t(OnlineDeliveryTabTitle)
        },
        {
            buttonId: 2,
            children: t(ClickAndCollectTabTitle)
        }
    ];

    async function handleOnClick() {
        if (selectedDeliveryMethod?.unavailableProducts) {
            GA4Tracking({
                eventName: 'go_to_anti_break_step_2',
                context: 'basket',
                category: 'ecommerce'
            });

            push(() => (
                <RemovedProductItems
                    model={selectedDeliveryMethod.unavailableProducts}
                    selectedDeliveryMethod={selectedDeliveryMethod}
                    selectedTab={selectedTab}
                />
            ));
        } else {
            if (isClickAndCollectDelivery(selectedDeliveryMethod)) {
                UpdateBasketStore({ storeId: selectedDeliveryMethod?.model.id });
            } else {
                await DeleteBasketStore();
            }

            const deliveryType = selectedTab === 1 ? 'Online' : 'ClickAndCollect';

            GA4Tracking({
                eventName: 'submit',
                context: 'basket',
                category: 'ecommerce',
                eventParams: [
                    { param_name: 'modal_type', param_value: 'anti_break' },
                    { param_name: 'delivery_method', param_value: deliveryType },
                    { param_name: 'removed_products', param_value: 0 }
                ]
            });
            setShowDeliveryFixBasketMessage(true);
            updateBasket();
            toggle(false);
        }
    }

    function tabContentRenderer() {
        switch (selectedTab) {
            case 1:
                return (
                    <OnlineDelivery
                        model={onlineDeliveryOption}
                        selectedDeliveryMethod={selectedDeliveryMethod}
                        setSelectedDeliveryMethod={setSelectedDeliveryMethod}
                    />
                );
            case 2:
                return (
                    <ClickAndCollectStores
                        model={clickAndCollectDeliveryOption}
                        selectedDeliveryMethodId={selectedDeliveryMethodId}
                        setSelectedDeliveryMethodId={setSelectedDeliveryMethodId}
                        setSelectedDeliveryMethod={setSelectedDeliveryMethod}
                    />
                );
            default:
                return <HighlightedText withBorder={false}>{t(NoDeliveryTabSelected)}</HighlightedText>;
        }
    }

    function handleTabSelection(buttonId) {
        setSelectedTab(buttonId);

        const tabName = buttonId === 1 ? t(OnlineDeliveryTabTitle) : t(ClickAndCollectTabTitle);

        GA4Tracking({
            eventName: 'change_tab',
            context: 'basket',
            category: 'ecommerce',
            eventParams: [{ param_name: 'tab_name', param_value: tabName }]
        });
    }

    return (
        <StyledFlexColumn gap="0">
            <ScrollContainer>
                <Typography.Headline size="M">{t(ModalHeading)}</Typography.Headline>
                <StyledBasketMessageContainer>
                    <Typography.Body as="span" size="M">
                        {format(disclaimer)}
                    </Typography.Body>
                </StyledBasketMessageContainer>
                <DeliveryOptionsContainer>
                    <ControlledSegmentedButtons
                        buttons={buttons}
                        activeId={selectedTab}
                        onChange={(buttonId) => handleTabSelection(buttonId)}
                        equalWidth
                    />
                    {tabContentRenderer()}
                </DeliveryOptionsContainer>
            </ScrollContainer>
            <ActionFooter size="lg" message={selectedDeliveryMethod?.disclaimer} messageType="warning">
                <ButtonContainer>
                    <Button size="lg" onClick={() => toggle(false)} variant="secondary">
                        {t(CancelButton)}
                    </Button>
                    <Button size="lg" onClick={handleOnClick} disabled={!selectedDeliveryMethod}>
                        {t(ContinueButton)}
                    </Button>
                </ButtonContainer>
            </ActionFooter>
        </StyledFlexColumn>
    );
}

export default AntiBreakQuickViewContent;
